// ============================================================================
// CENTRALIZED CONTENT CONFIGURATION
// ============================================================================
// Edit all website content in this single file
// All text, headings, descriptions, and data are managed here

export const siteContent = {
  // ==========================================================================
  // SITE METADATA
  // ==========================================================================
  site: {
    name: 'Zoraxo',
    tagline: 'Premier IT Solutions Provider',
    description:
      'Zoraxo Technology Private Limited is a premier provider of comprehensive technology solutions, specializing in Computer Systems, Peripherals, Accessories & Consumables, System Integration, CCTV Smart Surveillance, Wi-Fi Campus Network Design, IP Based PBX, PA System Integration, Smart Class Design, Biometric Integration, University/College Management Systems, Office Automation, Networking, Communication Products, Hardware Maintenance, Training, Business Alliances, Web Hosting, E-Commerce, Printing Services, Exam Material Supply, and more. We deliver end-to-end services from conceptualization and design to implementation and support, backed by industry-leading certifications and a commitment to innovation and customer satisfaction.',
  },
  // ========================================================================== 
  // SPECIALIZATIONS & SERVICES
  // ==========================================================================
  specializations: {
    overview: 'We offer a complete suite of IT solutions and services:',
    items: [
      'Computer Systems, Peripherals, Accessories & Consumables',
      'System Integration',
      'CCTV Smart Surveillance Implementation',
      'Wi-Fi Campus Network Design & Implementation',
      'IP Based PBX System Integration',
      'PA System Integration',
      'Smart Class Design & Implementation',
      'Biometric Implementation and integration with Attendance System',
      'University / College Management System (customized)',
      'Office Automation Systems',
      'Networking Solutions',
      'Communication Products',
      'Hardware Maintenance',
      'Training',
      'Business Alliances',
      'Web Hosting Solutions',
      'E-Commerce Application',
      'Printing Services (Books, Notebooks, Journals, Workbooks, Planners, Stationery, Labels, Stickers)',
      'Exam Material supply (Booklet & OMR Sheet)',
    ],
  },

  // ========================================================================== 
  // CERTIFICATIONS
  // ==========================================================================
  certifications: {
    items: [
      'ISO 9001:2015',
      'ISO 27001:2018',
      'CMMI MATURITY LEVEL 5',
    ],
  },

  // ========================================================================== 
  // SOLUTION DETAILS
  // ==========================================================================
  solutionDetails: {
    CCTV: {
      title: 'CCTV Solutions',
      description: 'Zoraxo is a premier provider of comprehensive technology solutions, specializing in CCTV, IP PBX, Networking (Wi-Fi Campus Designing), and Smart Class Designing. With a commitment to innovation and customer satisfaction, we offer end-to-end services from conceptualization and design to implementation and support.',
    },
    IPPBX: {
      title: 'IP PBX Solutions',
      description: 'Deployment of IP PBX systems tailored to business communication needs. VoIP solutions for cost-effective and efficient telephony. Unified communications for improved collaboration and productivity.',
    },
    Networking: {
      title: 'Networking (Wi-Fi Campus Designing)',
      description: 'Design and implementation of robust WiFi networks for campuses and large facilities. Network infrastructure planning, including routers, switches, and access points. Network security measures to safeguard against cyber threats and ensure data integrity.',
    },
    SmartClass: {
      title: 'Smart Class Designing',
      description: 'Integration of smart technologies in classrooms for interactive learning experiences. Installation of audio-visual equipment, interactive displays, and multimedia solutions. Training and support for educators to optimize the use of smart class technologies.',
    },
    OMR: {
      title: 'Optical Mark Reading Technology (OMR)',
      description: 'Technotouch Solutions represents Scantron Corporation, U.S.A for Optical Mark Readers. We provide scanning, processing, and evaluation of examination answer sheets and forms, with infrastructure to handle large-scale projects and decentralized scanning. Our team is trained to deliver projects of any size and magnitude.',
    },
  },

  // ==========================================================================
  // NAVIGATION
  // ==========================================================================
  navigation: {
    menuItems: [
      { label: 'Home', href: '#home' },
      { label: 'About', href: '#about' },
      { label: 'Services', href: '#services' },
      { label: 'Process', href: '#process' },
      { label: 'Testimonials', href: '#testimonials' },
      { label: 'Contact', href: '#contact' },
    ],
  },

  // ==========================================================================
  // HERO SECTION
  // ==========================================================================
  hero: {
    headline: {
      line1: 'Transforming Enterprises with',
      line2: 'AI, Digital Transformation,',
      line3: 'and Cloud Solutions',
    },
    subtext:
      'Empowering businesses with cutting-edge technology solutions, AI-driven innovations, and scalable cloud infrastructure to accelerate digital transformation and drive sustainable growth.',
    buttons: {
      primary: {
        text: 'Get Started',
        href: '#contact',
      },
      secondary: {
        text: 'Our Services',
        href: '#services',
      },
    },
  },

  // ==========================================================================
  // ABOUT SECTION
  // ==========================================================================
  about: {
    badge: 'ABOUT US',
    title: 'Comprehensive IT Solutions & Services',
    description:
      'We help businesses achieve strategic technology transformation, minimising the totality of their end-to-end business operations by leveraging modern technologies and innovative methodologies.',
    features: [
      {
        title: 'Industry Certifications',
        description: 'ISO 9001:2015, ISO 27001:2018, CMMI Level 5',
      },
      {
        title: 'Full IT Solution Suite',
        description: 'From hardware to software, printing, and exam materials',
      },
      {
        title: 'Expert Team',
        description: 'Experienced professionals and project managers',
      },
    ],
    stats: {
      experience: '14+ Years',
      projects: '500+ Projects',
      clients: '150+ Clients',
    },
    images: {
      image1: '/images/01.jpg',
      image1Alt: 'Team collaboration',
      image2: '/images/02.jpg',
      image2Alt: 'Professional workspace',
    },
  },

  // ==========================================================================
  // SERVICES SECTION
  // ==========================================================================
  services: {
    badge: 'OUR SERVICES',
    title: 'Innovative IT Solutions for Your Business',
    subtitle: 'Comprehensive technology services to drive your digital transformation',
    serviceList: [
      {
        id: 1,
        title: 'AI & Generative AI',
        description:
          'Develop AI for text, images, and audio. Enhance productivity and customer interactions with AI-powered automation and NLP.',
        items: [
          'Generative AI Models',
          'Machine Learning Solutions',
          'AI-Powered Automation',
          'Natural Language Processing',
        ],
        bgColor: 'bg-neutral-light-grey',
      },
      {
        id: 2,
        title: 'Technology Transformation',
        description:
          'Agile and scalable applications, AI-driven workflows, legacy modernization, and cybersecurity.',
        items: [
          'Cloud-Native Development',
          'Intelligent Automation',
          'Enterprise Application Modernization',
          'Cybersecurity & Compliance',
        ],
        bgColor: 'bg-white',
      },
      {
        id: 3,
        title: 'Innovation Management',
        description:
          'Adopt emerging technologies, rapid prototyping, and build sustainable competitive advantage.',
        items: [
          'Emerging Technology Adoption',
          'Design Thinking & Agile Methodologies',
          'Digital Product Innovation',
          'Corporate Innovation Programs',
        ],
        bgColor: 'bg-neutral-light-grey',
      },
      {
        id: 4,
        title: 'Cloud Services',
        description:
          'Optimize workflows, leverage AI and analytics, and enhance efficiency with digital solutions.',
        items: [
          'Process Mining & Optimization',
          'Data-Driven Decision Making',
          'Supply Chain & Logistics Optimization',
          'Workforce Productivity Tools',
        ],
        bgColor: 'bg-white',
      },
    ],
  },

  // ==========================================================================
  // STATS SECTION
  // ==========================================================================
  stats: {
    items: [
      { value: 14, label: 'Years', suffix: '+' },
      { value: 7, label: 'Locations', suffix: '+' },
      { value: 150, label: 'Satisfied Customers', suffix: '+' },
      { value: 7, label: "Technology CoE's", suffix: '+' },
      { value: 750, label: 'Employees', suffix: '+' },
    ],
  },

  // ==========================================================================
  // PROCESS SECTION
  // ==========================================================================
  process: {
    badge: 'WORKING PROCESS',
    title: 'Get your it solutions in 3 easy steps',
    subtitle: 'Our streamlined approach to delivering excellence',
    steps: [
      {
        id: '01',
        title: 'Understanding Story',
        description:
          'We help businesses achieve strategic technology transformation, minimising the totality of their end-to-end business',
        image: '/images/01(6).webp',
      },
      {
        id: '02',
        title: 'Start Working',
        description:
          'We help businesses achieve strategic technology transformation, minimising the totality of their end-to-end business',
        image: '/images/02(4).webp',
      },
      {
        id: '03',
        title: 'Review & Fixing',
        description:
          'We help businesses achieve strategic technology transformation, minimising the totality of their end-to-end business',
        image: '/images/03(4).webp',
      },
    ],
    videoSection: {
      title: 'Interested to Know More About Our Working Process?',
      buttonText: 'Work with us',
      buttonHref: '#contact',
    },
  },

  // ==========================================================================
  // TESTIMONIALS SECTION
  // ==========================================================================
  testimonials: {
    badge: 'OUR TESTIMONIAL',
    title: 'What our clients say about us',
    subtitle:
      "Accelerate innovation with world-class tech teams We'll match you to an entire remote team of incredible",
    reviews: [
      {
        id: 1,
        name: 'William Jekson',
        role: 'Product Manager',
        image: '/images/01(5).webp',
        rating: 5,
        text: 'I believe in lifelong learning and they are a great place to learn from experts. I have learned a lot and recommend it',
      },
      {
        id: 2,
        name: 'Sarah Anderson',
        role: 'CEO, Tech Solutions',
        image: '/images/testi1.png',
        rating: 5,
        text: 'Outstanding service and exceptional results. Their team delivered beyond our expectations and transformed our digital presence completely.',
      },
      {
        id: 3,
        name: 'Michael Chen',
        role: 'CTO, Innovation Labs',
        image: '/images/test2.png',
        rating: 5,
        text: 'Professional, reliable, and innovative. They understood our vision and brought it to life with cutting-edge technology solutions.',
      },
    ],
  },

  // ==========================================================================
  // CONTACT SECTION
  // ==========================================================================
  contact: {
    badge: 'CONTACT US',
    title: 'To Make Requests for Further Information, Contact Us',
    subtitle: 'Get in touch with our team for any inquiries',
    info: {
      phone: {
        label: 'Call Us',
        value: '+91 983 877 8808',
        href: 'tel:+919838778808',
      },
      email: {
        label: 'Email Us',
        value: 'info@zoraxo.com',
        href: 'mailto:info@zoraxo.com',
      },
      address: {
        label: 'Visit Us',
        value: 'Jankipuram Extension, Lucknow, Uttar Pradesh, India',
        href: 'https://www.google.com/maps?q=Jankipuram+Extension,+Lucknow,+Uttar+Pradesh,+India',
      },
    },
    form: {
      title: 'Send us a message',
      fields: {
        name: { label: 'Your Name', placeholder: 'Enter your name' },
        email: { label: 'Email Address', placeholder: 'Enter your email' },
        phone: { label: 'Phone Number', placeholder: 'Enter your phone' },
        subject: { label: 'Subject', placeholder: 'Enter subject' },
        message: {
          label: 'Your Message',
          placeholder: 'Write your message here...',
        },
      },
      submitButton: 'Send Message',
    },
  },

  // ==========================================================================
  // FOOTER
  // ==========================================================================
  footer: {
    brand: {
      name: 'ZORAXO',
      tagline: 'Technologies Pvt Ltd',
      description:
        'AI, Digital Transformation, and Cloud Solutions Accelerating Innovation with Cutting-Edge Technology and Tailored IT Services',
    },
    social: {
      facebook: '#',
      twitter: '#',
      instagram: '#',
      dribbble: '#',
      linkedin: '#',
    },
    sections: {
      services: {
        title: 'Our Services',
        links: [
          { label: 'AI & Machine Learning', href: '#' },
          { label: 'Cloud Solutions', href: '#' },
          { label: 'Digital Transformation', href: '#' },
          { label: 'Cybersecurity', href: '#' },
          { label: 'Data Analytics', href: '#' },
          { label: 'Software Development', href: '#' },
        ],
      },
      company: {
        title: 'Company',
        links: [
          { label: 'About Us', href: '#about' },
          { label: 'Our Team', href: '#' },
          { label: 'Careers', href: '#' },
          { label: 'Case Studies', href: '#' },
          { label: 'Blog', href: '#' },
          { label: 'Partners', href: '#' },
        ],
      },
      support: {
        title: 'Support',
        links: [
          { label: 'Help Center', href: '#' },
          { label: 'Documentation', href: '#' },
          { label: 'FAQs', href: '#' },
          { label: 'Contact Support', href: '#contact' },
          { label: 'Privacy Policy', href: '#' },
          { label: 'Terms of Service', href: '#' },
        ],
      },
    },
    contact: {
      title: 'Contact Info',
      email: 'info@zoraxo.com',
      phone: '+91 983 877 8808',
      address: 'Lucknow, Uttar Pradesh, India',
    },
    copyright: {
      year: new Date().getFullYear(),
      text: 'All rights reserved.',
      companyName: 'TECHNOTOUCH SOLUTION PVT. LTD.',
    },
  },

  // ==========================================================================
  // CTA (CALL TO ACTION) SECTION
  // ==========================================================================
  cta: {
    title: 'Ready to Transform Your Business?',
    subtitle: "Let's discuss how we can help you achieve your technology goals",
    buttonText: 'Get Started Today',
    buttonHref: '#contact',
  },

  // ==========================================================================
  // WHY CHOOSE US SECTION
  // ==========================================================================
  whyChooseUs: {
    badge: 'WHY CHOOSE US',
    title: 'Why Businesses Trust ZORAXO',
    subtitle: 'We deliver excellence through innovation and expertise',
    reasons: [
      {
        title: 'Expert Team',
        description: '750+ certified professionals with deep industry expertise',
        icon: 'team',
      },
      {
        title: 'Proven Track Record',
        description: '14+ years of delivering successful digital transformations',
        icon: 'award',
      },
      {
        title: 'Global Presence',
        description: '7+ locations serving clients worldwide',
        icon: 'globe',
      },
      {
        title: 'Innovation First',
        description: 'Cutting-edge AI and cloud technologies',
        icon: 'innovation',
      },
      {
        title: '24/7 Support',
        description: 'Round-the-clock expert technical assistance',
        icon: 'support',
      },
      {
        title: 'Quality Assurance',
        description: 'ISO certified processes and best practices',
        icon: 'quality',
      },
    ],
  },

  // ==========================================================================
  // PARTNERS SECTION
  // ==========================================================================
  partners: {
    badge: 'OUR PARTNERS',
    title: 'Trusted by Industry Leaders',
    subtitle: 'Working with the best to deliver excellence',
    logos: [
      { name: 'Partner 1', image: '/images/partner-1.png' },
      { name: 'Partner 2', image: '/images/partner-2.png' },
      { name: 'Partner 3', image: '/images/partner-3.png' },
      { name: 'Partner 4', image: '/images/partner-4.png' },
      { name: 'Partner 5', image: '/images/partner-5.png' },
    ],
  },

  // ==========================================================================
  // CASE STUDIES SECTION
  // ==========================================================================
  caseStudies: {
    badge: 'CASE STUDIES',
    title: 'Success Stories',
    subtitle: 'Real results from real projects',
    studies: [
      {
        id: 1,
        title: 'AI-Powered Customer Service Transformation',
        client: 'Fortune 500 Retail Company',
        industry: 'Retail',
        challenge:
          'High customer service costs and long response times affecting customer satisfaction',
        solution:
          'Implemented AI chatbot with NLP capabilities and automated ticketing system',
        results: [
          '60% reduction in response time',
          '40% cost savings',
          '85% customer satisfaction increase',
        ],
        image: '/images/case-study-1.jpg',
        technologies: ['AI', 'NLP', 'Cloud'],
      },
      {
        id: 2,
        title: 'Cloud Migration for Enterprise',
        client: 'Global Manufacturing Corporation',
        industry: 'Manufacturing',
        challenge: 'Legacy infrastructure limiting scalability and increasing maintenance costs',
        solution:
          'Complete cloud migration with hybrid architecture and microservices',
        results: [
          '50% infrastructure cost reduction',
          '99.9% uptime achieved',
          '3x faster deployment cycles',
        ],
        image: '/images/case-study-2.jpg',
        technologies: ['Cloud', 'DevOps', 'Microservices'],
      },
    ],
  },

  // ==========================================================================
  // BLOG SECTION
  // ==========================================================================
  blog: {
    badge: 'OUR BLOG',
    title: 'Latest Insights & Updates',
    subtitle: 'Stay informed about technology trends and best practices',
    posts: [
      {
        id: 1,
        title: 'The Future of AI in Enterprise Solutions',
        excerpt:
          'Exploring how artificial intelligence is transforming business operations and decision-making processes',
        author: 'John Doe',
        date: '2024-01-15',
        category: 'AI & ML',
        image: '/images/blog-1.jpg',
        readTime: '5 min read',
      },
      {
        id: 2,
        title: 'Cloud Security Best Practices for 2024',
        excerpt:
          'Essential security measures every organization should implement for cloud infrastructure',
        author: 'Jane Smith',
        date: '2024-01-10',
        category: 'Cloud Security',
        image: '/images/blog-2.jpg',
        readTime: '7 min read',
      },
      {
        id: 3,
        title: 'Digital Transformation Roadmap',
        excerpt:
          "A comprehensive guide to successfully navigating your organization's digital transformation journey",
        author: 'Mike Johnson',
        date: '2024-01-05',
        category: 'Digital Transformation',
        image: '/images/blog-3.jpg',
        readTime: '10 min read',
      },
    ],
  },

  // ==========================================================================
  // CAREERS SECTION
  // ==========================================================================
  careers: {
    badge: 'CAREERS',
    title: 'Join Our Team',
    subtitle: 'Be part of something extraordinary',
    description:
      "We're always looking for talented individuals who are passionate about technology and innovation. Join our growing team and make an impact.",
    benefits: [
      'Competitive salary packages',
      'Health insurance',
      'Flexible working hours',
      'Remote work options',
      'Learning & development programs',
      'Career growth opportunities',
    ],
    openPositions: [
      {
        id: 1,
        title: 'Senior Full Stack Developer',
        department: 'Engineering',
        location: 'Lucknow / Remote',
        type: 'Full-time',
        experience: '5+ years',
      },
      {
        id: 2,
        title: 'AI/ML Engineer',
        department: 'AI & Data Science',
        location: 'Lucknow / Remote',
        type: 'Full-time',
        experience: '3+ years',
      },
      {
        id: 3,
        title: 'Cloud Solutions Architect',
        department: 'Cloud Services',
        location: 'Lucknow / Remote',
        type: 'Full-time',
        experience: '7+ years',
      },
    ],
    cta: {
      text: 'View All Positions',
      href: '#careers',
    },
  },

  // ==========================================================================
  // ARTICLES SECTION
  // ==========================================================================
  articles: {
    badge: 'RESOURCES',
    title: 'Knowledge Hub',
    subtitle: 'Expert articles and technical resources',
    categories: [
      'All',
      'AI & Machine Learning',
      'Cloud Computing',
      'Cybersecurity',
      'DevOps',
      'Digital Transformation',
    ],
  },

  // ==========================================================================
  // EXPERIENCE SECTION
  // ==========================================================================
  experience: {
    badge: 'OUR EXPERIENCE',
    title: 'Decades of Excellence in Technology',
    subtitle: 'Building the future with proven expertise',
    timeline: [
      {
        year: '2010',
        title: 'Company Founded',
        description: 'Started with a vision to transform businesses through technology',
      },
      {
        year: '2015',
        title: 'Global Expansion',
        description: 'Opened offices in 5 new countries',
      },
      {
        year: '2020',
        title: 'AI Innovation Center',
        description: 'Launched dedicated AI and ML research facility',
      },
      {
        year: '2024',
        title: 'Industry Leader',
        description: 'Recognized as top provider of enterprise digital solutions',
      },
    ],
  },

  // ==========================================================================
  // TOPBAR
  // ==========================================================================
  topbar: {
    email: 'info@zoraxo.com',
    phone: '+91 983 877 8808',
    workingHours: 'Mon - Fri: 9:00 AM - 6:00 PM',
    socialLinks: {
      facebook: '#',
      twitter: '#',
      linkedin: '#',
      instagram: '#',
    },
  },
};

// Type definitions for better TypeScript support
export type SiteContent = typeof siteContent;
export type NavigationItem = typeof siteContent.navigation.menuItems[0];
export type Service = typeof siteContent.services.serviceList[0];
export type Testimonial = typeof siteContent.testimonials.reviews[0];
export type ProcessStep = typeof siteContent.process.steps[0];
export type Stat = typeof siteContent.stats.items[0];
export type CaseStudy = typeof siteContent.caseStudies.studies[0];
export type BlogPost = typeof siteContent.blog.posts[0];
export type Career = typeof siteContent.careers.openPositions[0];
